"use client";

import {
  SetReflectionRequest,
  ThoughtDimensionAttributes,
} from "@/generated/fastapi";
import { useSetReflectionMutation } from "@/features/thoughts";
import {
  getDefaultAssessmentInstrumentId,
  getLikert5pAgreementOptions,
} from "@/shared/config/backend";
import { Box, HStack, RadioGroup, Text, VStack } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import type { QuestionnaireDimensionOriented } from "../types";
import { useAnswers } from "../hooks/useAnswers";
import {
  QUESTIONNAIRE_CHECKMARK_COLOR,
  QUESTIONNAIRE_CHECKMARK_POSITION,
  QUESTIONNAIRE_CHECKMARK_SIZE,
  QUESTIONNAIRE_SHOW_CHECKMARK,
  QUESTIONNAIRE_SUCCESS_BG_COLOR,
} from "../config";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { normalizeIconSize } from "@/shared/ui/actions/GenericIcon";
import type { ResponsiveIconSize } from "@/shared/ui/actions/GenericIcon";
import { showErrorToast } from "@/shared/feedback";
import ReflectionFeedbackAction from "./ReflectionFeedbackAction";

// Sub-component for individual thought item with animation
function DimensionThoughtItem({
  thought,
  dimension,
  question,
  iconSize,
  onAnswerProvided,
}: {
  thought: { id: string; mindstring: string; currentScore: number | null };
  dimension: string;
  question: string;
  iconSize: ResponsiveIconSize;
  onAnswerProvided?: (thoughtId: string, dimension: string) => void;
}) {
  const { isAnswered, shouldShow, animationStyles, handleAnswer } =
    useAnswers();
  const setReflectionMutation = useSetReflectionMutation();
  const normalizedIconSize = normalizeIconSize(iconSize);

  const [score, setScore] = useState<string | null>(
    thought.currentScore?.toString() || null,
  );

  const handleScoreChange = useCallback(
    (value: string) => {
      if (!value) return;

      setScore(value);

      const request: SetReflectionRequest = {
        thought_id: thought.id,
        dimension_index: dimension as ThoughtDimensionAttributes,
        score: parseInt(value, 10),
        instrument: getDefaultAssessmentInstrumentId(),
      };

      setReflectionMutation
        .mutateAsync(request)
        .then(() => {
          handleAnswer(() => {
            setScore(null);
            onAnswerProvided?.(thought.id, dimension);
          });
        })
        .catch(() => {
          showErrorToast("Failed to save reflection.");
        });
    },
    [
      thought.id,
      dimension,
      onAnswerProvided,
      handleAnswer,
      setReflectionMutation,
    ],
  );

  if (!shouldShow) return null;

  return (
    <Box
      borderRadius="md"
      position="relative"
      bg={isAnswered ? QUESTIONNAIRE_SUCCESS_BG_COLOR : "transparent"}
      style={animationStyles}
    >
      {/* Checkmark icon overlay */}
      {isAnswered && QUESTIONNAIRE_SHOW_CHECKMARK && (
        <Box
          position="absolute"
          top={QUESTIONNAIRE_CHECKMARK_POSITION === "top-right" ? 2 : "50%"}
          right={QUESTIONNAIRE_CHECKMARK_POSITION === "top-right" ? 2 : "50%"}
          transform={
            QUESTIONNAIRE_CHECKMARK_POSITION === "center"
              ? "translate(50%, -50%)"
              : undefined
          }
          zIndex={10}
        >
          <GenericIcon
            icon="confirm"
            size={QUESTIONNAIRE_CHECKMARK_SIZE}
            color={QUESTIONNAIRE_CHECKMARK_COLOR}
          />
        </Box>
      )}

      <HStack align="start" justify="space-between" gap="2">
        <Text fontSize="sm" fontWeight="medium" flex="1" minW="0">
          {thought.mindstring}
        </Text>
        <ReflectionFeedbackAction
          thoughtId={thought.id}
          thoughtWording={thought.mindstring}
          dimensionKey={dimension as ThoughtDimensionAttributes}
          questionSnapshot={question}
        />
      </HStack>

      <RadioGroup.Root
        value={score}
        onValueChange={(e) => handleScoreChange(e.value || "")}
        disabled={isAnswered}
      >
        <HStack>
          {getLikert5pAgreementOptions().map((item) => (
            <Box key={item.value} padding="1" flex="1" position="relative">
              <RadioGroup.Item value={item.value} width="full" height="full">
                <RadioGroup.ItemHiddenInput />
                <Box
                  // @ts-ignore -- Chakra accepts runtime color strings here.
                  color={item.color}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <GenericIcon
                    icon={item.icon}
                    humanReadable={item.label}
                    size={normalizedIconSize}
                  />
                </Box>
              </RadioGroup.Item>
            </Box>
          ))}
        </HStack>
      </RadioGroup.Root>
    </Box>
  );
}

interface QuestionnaireDimensionItemProps {
  dimensionData: QuestionnaireDimensionOriented;
  iconSize?: ResponsiveIconSize;
  onAnswerProvided?: (thoughtId: string, dimension: string) => void;
}

export default function QuestionnaireDimensionItem({
  dimensionData,
  iconSize = "md",
  onAnswerProvided,
}: QuestionnaireDimensionItemProps) {
  return (
    <VStack align="stretch">
      {dimensionData.thoughts.map((thought) => (
        <DimensionThoughtItem
          key={thought.id}
          thought={thought}
          dimension={dimensionData.dimension}
          question={dimensionData.question}
          iconSize={iconSize}
          onAnswerProvided={onAnswerProvided}
        />
      ))}
    </VStack>
  );
}
